import { useState, useEffect } from 'react'
import AdminSidebar from '../../components/admin/AdminSidebar'
import './AdminSettingsPage.css'
import { apiFetch } from '../../utils/api'

// const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const MAX_PROJECTS = 3

export default function AdminSettingsPage() {
  const [settings, setSettings]   = useState({})
  const [loading, setLoading]     = useState(true)
  const [saving, setSaving]       = useState(false)
  const [saved, setSaved]         = useState(false)
  const [error, setError]         = useState(null)

  const active = parseInt(settings.active_projects || 0)
  const atCapacity = active >= MAX_PROJECTS

  useEffect(() => {
    apiFetch('/api/admin/settings')
      .then(r => r.json())
      .then(d => setSettings(d.settings || {}))
      .catch(() => setError('Could not load settings.'))
      .finally(() => setLoading(false))
  }, [])

  const updateSetting = async (key, value) => {
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const res = await apiFetch(`/api/admin/settings/${key}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: String(value) }),
      })
      if (!res.ok) throw new Error('Failed to save.')
      setSettings(s => ({ ...s, [key]: String(value) }))
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const increment = () => { if (active < MAX_PROJECTS) updateSetting('active_projects', active + 1) }
  const decrement = () => { if (active > 0) updateSetting('active_projects', active - 1) }

  return (
    <div className='as-shell'>
      <AdminSidebar />

      <main className='as-main'>
        <div className='as-topbar'>
          <div>
            <h1 className='as-title'>Settings</h1>
            <p className='as-sub'>Control how your site behaves</p>
          </div>
          {saved && <span className='as-saved'> Saved</span>}
        </div>

        {loading && <div className='as-state'><div className='as-spinner'></div><p>Loading…</p></div>}
        {error   && <div className='as-state as-state--error'><p> {error}</p></div>}

        {!loading && (
          <div className='as-sections'>

            {/* ── Active projects ── */}
            <div className='as-section'>
              <div className='as-section-header'>
                <div>
                  <h2>Active Projects</h2>
                  <p>
                    Set how many client projects you're currently handling.
                    When this reaches <strong>{MAX_PROJECTS}</strong>, visitors will see a
                    capacity warning before submitting a quote.
                  </p>
                </div>
                <div className={`as-capacity-badge ${atCapacity ? 'as-capacity-badge--full' : ''}`}>
                  {atCapacity ? ' At Capacity' : ' Taking Projects'}
                </div>
              </div>

              <div className='as-counter-row'>
                <button
                  className='as-counter-btn'
                  onClick={decrement}
                  disabled={active === 0 || saving}
                  aria-label='Decrease'
                >−</button>

                <div className='as-counter-display'>
                  <span className='as-counter-num'>{active}</span>
                  <span className='as-counter-max'>/ {MAX_PROJECTS}</span>
                </div>

                <button
                  className='as-counter-btn'
                  onClick={increment}
                  disabled={active >= MAX_PROJECTS || saving}
                  aria-label='Increase'
                >+</button>
              </div>

              {/* Visual progress bar */}
              <div className='as-progress-wrap'>
                <div
                  className='as-progress-fill'
                  style={{
                    width: `${(active / MAX_PROJECTS) * 100}%`,
                    background: atCapacity ? '#ff8080' : active === MAX_PROJECTS - 1 ? '#ffc850' : '#FFB7CE',
                  }}
                ></div>
              </div>
              <p className='as-progress-label'>
                {active === 0 && 'You have no active projects — all slots are open.'}
                {active === 1 && '1 slot taken — 2 slots remaining.'}
                {active === 2 && '2 slots taken — 1 slot remaining.'}
                {active === MAX_PROJECTS && 'All slots taken — new quote submissions will see the capacity warning.'}
              </p>
            </div>

            {/* ── How it works ── */}
            <div className='as-section as-section--info'>
              <h2>How the capacity warning works</h2>
              <div className='as-info-steps'>
                <div className='as-info-step'>
                  <span className='as-info-num'>1</span>
                  <p>You set the active project count here whenever you take on or complete a project.</p>
                </div>
                <div className='as-info-step'>
                  <span className='as-info-num'>2</span>
                  <p>When count = {MAX_PROJECTS}, visitors clicking "Get Quote" see a popup explaining you're at capacity.</p>
                </div>
                <div className='as-info-step'>
                  <span className='as-info-num'>3</span>
                  <p>They can still submit their brief — it gets saved and you reach out when a slot opens up.</p>
                </div>
                <div className='as-info-step'>
                  <span className='as-info-num'>4</span>
                  <p>When you finish a project, reduce the count here and the warning disappears automatically.</p>
                </div>
              </div>
            </div>

          </div>
        )}
      </main>
    </div>
  )
}